/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import {
    EquipmentRowData,
    FileImportResult,
    FileValidationResult,
    UploadedFileMeta,
    ValidationIssue,
    ValidationRow,
    WizardFileId
} from "./types";

type UploadResponseDTO = {
    files: ApiUploadedFileMetaDTO[];
};

type ApiUploadedFileMetaDTO = {
    id: string;
    name: string;
    size: number;
    type: string;
};

type ValidateRequestDTO = {
    files: { id: string }[];
};

type ValidateResponseDTO = {
    results: FileValidationResultDTO[];
};

type FileValidationResultDTO = {
    fileId: string;
    isValid: boolean;
    rowsToFix: ValidationRowDTO[];
    issues: ValidationIssueDTO[];
};

type ValidationRowDTO = {
    rowId: string;
    rowIndex: number;
    data: EquipmentRowData;
};

type ValidationIssueDTO = {
    id: string;
    fileId: string;
    rowId: string;
    rowIndex: number;
    field: string;
    message: string;
    severity: string;
    resolved: boolean;
};

type RevalidateRequestDTO = {
    fileId: string;
    rows: ValidationRowDTO[];
};

type ImportRequestDTO = {
    files: { fileId: string }[];
};

type ImportResponseDTO = {
    results: FileImportResultDTO[];
};

type FileImportResultDTO = {
    fileId: string;
    success: boolean;
    message: string;
};

const mapSeverity = (s: string): "error" | "warning" => {
    const lower = (s ?? "").toLowerCase();
    return lower === "warning" ? "warning" : "error";
};

const mapFileValidationResult = (dto: FileValidationResultDTO): FileValidationResult => {
    const rows: ValidationRow<EquipmentRowData>[] = (dto.rowsToFix ?? []).map((r) => ({
        rowId: r.rowId,
        rowIndex: r.rowIndex,
        data: (r.data ?? {}) as EquipmentRowData
    }));

    const issues: ValidationIssue[] = (dto.issues ?? []).map((i) => ({
        id: i.id,
        fileId: i.fileId,
        rowId: i.rowId,
        rowIndex: i.rowIndex,
        field: i.field as any,
        message: i.message,
        severity: mapSeverity(i.severity),
        resolved: !!i.resolved
    }));

    return {
        isValid: !!dto.isValid,
        rowsToFix: dto.isValid ? [] : rows,
        issues: dto.isValid ? [] : issues
    };
};

const mapFileImportResult = (dto: FileImportResultDTO): FileImportResult => ({
    success: !!dto.success,
    message: dto.message ?? ""
});

export async function uploadEquipmentFiles(files: File[]): Promise<UploadedFileMeta[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    const { data } = await axios.post<UploadResponseDTO>(
        "/api/import/equipments/files",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
    );

    const metas = data?.files ?? [];

    return metas.map((m, idx) => {
        const original =
            files[idx] ??
            files.find((f) => f.name === m.name && f.size === m.size) ??
            new File([], m.name, { type: m.type });

        return {
            id: m.id,
            file: original,
            name: m.name,
            size: m.size,
            type: m.type
        };
    });
}

export async function validateEquipments(fileIds: WizardFileId[]): Promise<Record<WizardFileId, FileValidationResult>> {
    const body: ValidateRequestDTO = { files: fileIds.map((id) => ({ id })) };

    const { data } = await axios.post<ValidateResponseDTO>(
        "/api/import/equipments/validate",
        body
    );

    const byId: Record<WizardFileId, FileValidationResult> = {};
    (data?.results ?? []).forEach((r) => {
        byId[r.fileId] = mapFileValidationResult(r);
    });

    return byId;
}

export async function revalidateEquipment(fileId: WizardFileId, rows: ValidationRow<EquipmentRowData>[]): Promise<FileValidationResult> {
    const body: RevalidateRequestDTO = {
        fileId,
        rows: (rows ?? []).map((r) => ({
            rowId: r.rowId,
            rowIndex: r.rowIndex,
            data: { ...(r.data as any) } as EquipmentRowData
        }))
    };

    const { data } = await axios.post<FileValidationResultDTO>(
        "/api/import/equipments/revalidate",
        body
    );

    return mapFileValidationResult(data);
}

export async function importEquipments(fileIds: WizardFileId[]): Promise<Record<WizardFileId, FileImportResult>> {
    const body: ImportRequestDTO = { files: fileIds.map((fileId) => ({ fileId })) };
    try {
        const { data } = await axios.post<ImportResponseDTO>(
            "/api/import/equipments/import",
            body
        );

        const byId: Record<WizardFileId, FileImportResult> = {};
        (data?.results ?? []).forEach((r) => {
            byId[r.fileId] = mapFileImportResult(r);
        });

        return byId;
    } catch (e: any) {
        console.log("IMPORT URL:", e?.config?.baseURL, e?.config?.url);
        console.log("IMPORT BODY:", e?.config?.data);
        console.log("IMPORT RESPONSE:", e?.response?.status, e?.response?.data);
        throw e;
    }
}